import {Badge} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Grid} from 'src/design/atoms/Grid';
import {List} from 'src/design/atoms/List';
import {Stack} from 'src/design/atoms/Stack';
import {IconHistory, IconId} from '@tabler/icons-react';
import React, {useState} from 'react';
import {Button} from 'src/design/atoms/Button';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {
  useGeneratePersonnelBadgeMutation,
  useGetPersonnelBadgeHistoryQuery,
  useGetPersonnelBadgeQuery,
  useLazyGetPersonnelBadgeQuery,
} from 'src/redux/queries/personnelBadges';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {printBadgeArtifact} from 'src/shared/utils/printBadgeArtifact';
import {BadgeArtifactPreview} from './BadgeArtifactPreview';
import {BadgeCompareModal} from './BadgeCompareModal';
import {BadgeHistoryTable} from './BadgeHistoryTable';
import {useBadgeArtifactHtml} from './useBadgeArtifactHtml';

export interface PrintBadgeSectionProps {
  personnelId: number;
}

// Rendered by `PersonnelBadgeAccessTab` only when the instance's
// `BadgePrinting` feature flag is on and the viewer has the
// `personnel_print_badge` permission.
export const PrintBadgeSection: React.FC<PrintBadgeSectionProps> = ({
  personnelId,
}) => {
  const {setAlert} = useAlert();
  const [generationWarnings, setGenerationWarnings] = useState<string[]>([]);
  const [printingBadgeId, setPrintingBadgeId] = useState<number | undefined>(
    undefined
  );
  const [compareBadgeId, setCompareBadgeId] = useState<number | undefined>(
    undefined
  );
  const [compareModalOpen, setCompareModalOpen] = useState(false);

  const {
    data: history = [],
    isLoading: isHistoryLoading,
    isFetching: isHistoryFetching,
  } = useGetPersonnelBadgeHistoryQuery(personnelId, {
    // Badge regeneration runs as a background job after personnel saves, so
    // a cached history can be stale by the time the user opens this tab.
    refetchOnMountOrArgChange: true,
  });

  const [generateBadge, {isLoading: isGenerating}] =
    useGeneratePersonnelBadgeMutation();
  const [fetchBadge] = useLazyGetPersonnelBadgeQuery();

  const latest = history[0];

  const {data: latestDetail, isFetching: isLatestDetailFetching} =
    useGetPersonnelBadgeQuery(
      {id: personnelId, badgeId: latest?.id as number},
      {skip: latest === undefined}
    );

  const latestArtifact = useBadgeArtifactHtml(latestDetail?.downloadUrl);

  const handleGenerate = async () => {
    try {
      const result = await generateBadge({
        id: personnelId,
        model: {},
      }).unwrap();
      setGenerationWarnings(result.warnings ?? []);
      setAlert('Badge generated successfully.', 'success');
    } catch (error) {
      setGenerationWarnings([]);
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const printBadgeById = async (badgeId: number) => {
    setPrintingBadgeId(badgeId);
    try {
      const html =
        badgeId === latestDetail?.id && latestArtifact.html
          ? latestArtifact.html
          : await (async () => {
              const detail = await fetchBadge({
                id: personnelId,
                badgeId,
              }).unwrap();
              const response = await fetch(detail.downloadUrl);
              if (!response.ok) {
                throw new Error(
                  `Failed to load badge artifact (${response.status})`
                );
              }
              return response.text();
            })();

      printBadgeArtifact(html);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    } finally {
      setPrintingBadgeId(undefined);
    }
  };

  const handleCompare = (badgeId: number) => {
    setCompareBadgeId(badgeId);
    setCompareModalOpen(true);
  };

  const isLoadingHistory = isHistoryLoading || isHistoryFetching;

  return (
    <Stack gap="lg" data-testid="print-badge-section">
      {generationWarnings.length > 0 && (
        <Alert
          color="yellow"
          variant="light"
          title="Badge generated with warnings"
          data-testid="badge-generation-warnings">
          <List size="sm">
            {generationWarnings.map(warning => (
              <List.Item key={warning}>{warning}</List.Item>
            ))}
          </List>
        </Alert>
      )}

      <Grid gutter="lg">
        <Grid.Col span={{base: 12, md: 5}}>
          <SectionCard
            icon={<IconId size={16} />}
            title="Current Badge"
            h="100%"
            rightSection={
              <Button loading={isGenerating} onClick={handleGenerate}>
                Generate Badge
              </Button>
            }>
            <Box p="md">
              <BadgeArtifactPreview
                html={latestArtifact.html}
                isLoading={
                  isLoadingHistory ||
                  isLatestDetailFetching ||
                  latestArtifact.isLoading
                }
                error={latestArtifact.error}
                emptyMessage={
                  !isLoadingHistory && history.length === 0
                    ? 'No badge has been generated yet. Click "Generate Badge" to create one.'
                    : undefined
                }
                onPrint={() => latest && printBadgeById(latest.id)}
                printDisabled={printingBadgeId !== undefined}
              />
            </Box>
          </SectionCard>
        </Grid.Col>

        <Grid.Col span={{base: 12, md: 7}}>
          <SectionCard
            icon={<IconHistory size={16} />}
            title="Version History"
            h="100%"
            rightSection={
              history.length > 0 && (
                <Badge size="lg" variant="light" color="gray" circle>
                  {history.length}
                </Badge>
              )
            }>
            <Box p="md">
              <BadgeHistoryTable
                history={history}
                printingBadgeId={printingBadgeId}
                compareBadgeId={compareBadgeId}
                isLoading={isLoadingHistory}
                onPrint={printBadgeById}
                onCompare={handleCompare}
              />
            </Box>
          </SectionCard>
        </Grid.Col>
      </Grid>

      <BadgeCompareModal
        opened={compareModalOpen}
        personnelId={personnelId}
        history={history}
        initialLeftBadgeId={compareBadgeId}
        initialRightBadgeId={latest?.id}
        onClose={() => setCompareModalOpen(false)}
      />
    </Stack>
  );
};
